import { useState, useEffect, useCallback } from 'react';
import { getCurrentUser, login as apiLogin, logout as apiLogout } from '../api/endpoints';
import { SESSION_EXPIRED_EVENT } from '../api/client';
import { invalidateCachedFetch } from './useCachedFetch';
import type { User } from '../types';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await getCurrentUser();
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // When any API call reports the session is gone (see client.ts response
  // interceptor), drop back to the login screen and wipe per-user caches so the
  // next sign-in starts clean. Idempotent: concurrent in-flight 403s may fire
  // this more than once.
  useEffect(() => {
    const onSessionExpired = () => {
      invalidateCachedFetch();
      setUser(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const login = async (username: string, password: string) => {
    // Wipe the SWR caches on identity change — on a shared clinical
    // workstation the previous user's cached dashboard/analytics must not
    // flash for the next account.
    invalidateCachedFetch();
    const response = await apiLogin(username, password);
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    // Always clear local auth state, even if the logout request fails (e.g. the
    // session is already dead and the server answers 403). Otherwise the user is
    // stranded looking logged-in with no way back to the login screen.
    try {
      await apiLogout();
    } finally {
      invalidateCachedFetch();
      setUser(null);
    }
  };

  return { user, loading, login, logout, checkAuth };
}
